import * as THREE from 'three'
import { injectSunHaze } from './atmosphere'
import { mustReplace } from './shaderPatch'

/**
 * The snow surface material.
 *
 * A physically-shaded base (so it picks up the sky probe and casts/receives a
 * real sun shadow) with two things injected that snow specifically needs and
 * that no stock material provides:
 *
 *   **Procedural detail normals.** The mesh is metres per cell; snow's
 *   character lives at ten to fifty centimetres — sastrugi ridges, wind ripple,
 *   the grain left where sluff has run. Carrying that in geometry would cost a
 *   hundred times the triangles, and carrying it only in vertex colour (as this
 *   did before) shades perfectly flat, which is why the surface read as painted
 *   rather than sculpted. Here it perturbs the actual shading normal, computed
 *   from world position so it needs no UVs and no texture.
 *
 *   **Per-vertex roughness.** Powder is near-Lambertian; wind crust and ice are
 *   glossy enough to throw a hard sun glint. Sharing one roughness across the
 *   whole snowpack throws away most of what distinguishes the four surfaces,
 *   and those differences are load-bearing here — they are what the player is
 *   reading the mountain for.
 *
 * The detail is modulated per-vertex too, so ripple appears where the wind has
 * actually worked the snow rather than uniformly across the face.
 */
export function createSnowMaterial(
  detailFadeStart = 70,
  detailFadeEnd = 220,
): THREE.MeshStandardMaterial {
  // Photographed snow (ambientCG Snow006, CC0), fetched at runtime now that
  // the game no longer has to fit in one offline file. The flag gates the
  // shader until both maps are actually decoded — three samples an unloaded
  // texture as black, and a black-modulated mountain is worse than a late one.
  const texOn = { value: 0 }
  let pending = 3
  const arrived = (): void => { if (--pending === 0) texOn.value = 1 }
  const loader = new THREE.TextureLoader()
  const snowMap = loader.load('tex/snow-color.jpg', arrived)
  snowMap.wrapS = snowMap.wrapT = THREE.RepeatWrapping
  snowMap.colorSpace = THREE.SRGBColorSpace
  snowMap.anisotropy = 4
  const snowNormal = loader.load('tex/snow-normal.jpg', arrived)
  snowNormal.wrapS = snowNormal.wrapT = THREE.RepeatWrapping
  snowNormal.anisotropy = 4
  // The same photographed rock the boulders wear (ambientCG Rock031), now on
  // the terrain's cliff bands — at 1K, since the single-file constraint died.
  const rockMap = loader.load('tex/rock-color.jpg', arrived)
  rockMap.wrapS = rockMap.wrapT = THREE.RepeatWrapping
  rockMap.colorSpace = THREE.SRGBColorSpace
  rockMap.anisotropy = 4

  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.85,
    metalness: 0.0,
    // Snow is a dense scatterer; a little sheen at grazing angles is most of
    // what separates it from matte white plastic.
    envMapIntensity: 1.0,
  })

  mat.onBeforeCompile = (shader) => {
    injectSunHaze(shader)
    shader.uniforms.uFadeStart = { value: detailFadeStart }
    shader.uniforms.uFadeEnd = { value: detailFadeEnd }
    shader.uniforms.uSnowMap = { value: snowMap }
    shader.uniforms.uSnowNormalMap = { value: snowNormal }
    shader.uniforms.uRockMap = { value: rockMap }
    shader.uniforms.uSnowTexOn = texOn

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
         attribute vec3 aSnow;
         varying vec3 vSnow;
         varying vec3 vWorldPos;
         varying vec3 vWorldNormal;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
         vSnow = aSnow;
         vWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
         // The terrain's normal attribute is baked in world space (the mesh
         // sits untransformed in world coordinates), so it can feed the
         // triplanar weights directly.
         vWorldNormal = objectNormal;`,
      )

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
         varying vec3 vSnow;
         varying vec3 vWorldPos;
         uniform float uFadeStart;
         uniform float uFadeEnd;
         uniform sampler2D uSnowMap;
         uniform sampler2D uSnowNormalMap;
         uniform sampler2D uRockMap;
         uniform float uSnowTexOn;
         varying vec3 vWorldNormal;
         // Shared between injection points; color_fragment runs first and
         // fills them, the roughness and normal stages reuse them.
         float flCamDist;
         float flDetailFade;
         float flSnowMask;

         // Arithmetic, not sin-based. The call tree above multiplies this
         // brutally: four hashes per noise lookup, three octaves per detail
         // sample, and three samples to take the gradient — 36 evaluations for
         // every snow pixel and 72 for every rock pixel. With a
         // fract(sin(dot(...))) hash that was 36 transcendentals per pixel,
         // and at a flagship's 3.3M pixels roughly 119 million sin() calls per
         // frame before the shadow pass had even started. This costs about six
         // arithmetic ops instead. The pattern it produces differs from the old
         // one, but its character — and everything tuned against it — does not.
         float snowHash(vec2 p) {
           vec3 p3 = fract(vec3(p.x, p.y, p.x) * 0.1031);
           p3 += dot(p3, p3.yzx + 33.33);
           return fract((p3.x + p3.y) * p3.z);
         }
         float snowNoise(vec2 p) {
           vec2 i = floor(p), f = fract(p);
           f = f * f * (3.0 - 2.0 * f);
           float a = snowHash(i);
           float b = snowHash(i + vec2(1.0, 0.0));
           float c = snowHash(i + vec2(0.0, 1.0));
           float d = snowHash(i + vec2(1.0, 1.0));
           return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
         }
         // Wind-worked snow ripples across the prevailing wind, so the octaves
         // are deliberately anisotropic rather than round.
         float snowDetail(vec2 p) {
           return snowNoise(p * vec2(1.0, 2.6)) * 0.55
                + snowNoise(p * vec2(2.7, 6.1) + 31.4) * 0.3
                + snowNoise(p * vec2(7.9, 14.0) - 12.7) * 0.15;
         }
         // Rock breaks rather than ripples. Ridged noise — the absolute value of
         // a signed field — creates creases where the field crosses zero, which
         // is what reads as fracture; smooth fbm at any amplitude just reads as
         // more snow. Deliberately coarser than the snow detail, because a rock
         // band that carries the same feature size as sastrugi looks like a
         // dirty snowfield rather than a cliff.
         float rockDetail(vec2 p) {
           float a = abs(snowNoise(p * 0.42) - 0.5) * 2.0;
           float b = abs(snowNoise(p * 1.30 + 7.3) - 0.5) * 2.0;
           float c = snowNoise(p * 3.60 - 3.1);
           return a * 0.50 + b * 0.32 + c * 0.18;
         }`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
         {
           flCamDist = length(vWorldPos - cameraPosition);
           flDetailFade = 1.0 - smoothstep(uFadeStart, uFadeEnd, flCamDist);
           // Bare rock keeps its own material; everything else is snowpack.
           flSnowMask = 1.0 - clamp(vSnow.z * 2.0, 0.0, 1.0);

           // Photographed snow albedo at two scales — 3m tiles carry the
           // grain, 14m tiles break the repeat — normalised by the map's
           // measured mean (0.584 linear) so it modulates the baked colour
           // rather than darkening it. The near scale fades with the detail
           // normals; the broad scale runs far enough to texture the whole
           // corridor wall.
           vec2 texUv = vWorldPos.xz;
           vec3 texA = texture2D(uSnowMap, texUv / 3.1).rgb;
           vec3 texB = texture2D(uSnowMap, texUv / 13.7 + 0.37).rgb;
           float farFade = 1.0 - smoothstep(uFadeEnd * 1.5, uFadeEnd * 3.5, flCamDist);
           vec3 modA = mix(vec3(1.0), texA / 0.584, flDetailFade);
           vec3 modB = mix(vec3(1.0), texB / 0.584, farFade * 0.7);
           diffuseColor.rgb *= mix(vec3(1.0), modA * modB, uSnowTexOn * flSnowMask);

           // The cliff bands wear the boulders' photograph, triplanar from
           // world space — cliffs are near-vertical, so the flat XZ projection
           // the snow uses would smear down them; three projections weighted
           // by the world normal have no bad angle. Normalised by the map's
           // measured mean (0.176, from tools/make-rocktex.mjs) so it
           // modulates the baked tonal variation rather than darkening it.
           float rockM = clamp(vSnow.z * 2.0, 0.0, 1.0);
           if (rockM > 0.01) {
             vec3 w = abs(vWorldNormal);
             w /= (w.x + w.y + w.z + 1e-5);
             vec3 rx = texture2D(uRockMap, vWorldPos.zy * 0.31).rgb;
             vec3 ry = texture2D(uRockMap, vWorldPos.xz * 0.31).rgb;
             vec3 rz = texture2D(uRockMap, vWorldPos.xy * 0.31).rgb;
             // Contrast pulled well in: the photograph carries deep crevice
             // blacks, and multiplying them into the already-dark baked rock
             // rendered the cliff bands as coal against mid-grey boulders in
             // the same frame. Half-strength modulation keeps the grain and
             // drops the coal.
             vec3 rock = min((rx * w.x + ry * w.y + rz * w.z) / 0.176, vec3(2.2));
             rock = max(mix(vec3(1.0), rock, 0.55), vec3(0.34));
             diffuseColor.rgb *= mix(vec3(1.0), rock, uSnowTexOn * rockM * farFade);
           }
         }`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
         roughnessFactor = vSnow.y;
         {
           // Sparkle. Real snow is full of specular grains that flash and die
           // as the camera moves. Faked properly it needs no extra light:
           // scatter a few percent of 3cm cells with near-mirror roughness,
           // and the PBR sun specular does the rest — glints appear exactly
           // where a facet happens to align, and shift as you ski.
           float cell = snowHash(floor(vWorldPos.xz * 29.0));
           float glint = step(0.982, cell);
           roughnessFactor = mix(roughnessFactor, 0.10,
             glint * flDetailFade * flSnowMask * uSnowTexOn);
         }`,
      )
      .replace(
        '#include <normal_fragment_maps>',
        `#include <normal_fragment_maps>
         {
           vec2 uv = vWorldPos.xz;
           // Fade the procedural detail out with distance. It runs at 10-50cm,
           // so past a hundred metres or so a whole cycle of it is well under a
           // pixel — which does not read as detail, it reads as noise, and it
           // crawls as the camera moves. Costs nothing and it is the difference
           // between a calm distant slope and a shimmering one. It matters more
           // the sharper the screen: at 2.5x pixel ratio there are far more
           // sub-pixel cycles on screen than there were at 1.5x.
           float camDist = length(vWorldPos - cameraPosition);
           float detailFade = 1.0 - smoothstep(uFadeStart, uFadeEnd, camDist);
           float snowStrength = vSnow.x * detailFade;
           float rockStrength = vSnow.z * 1.35 * detailFade;
           vec3 grad = vec3(0.0);

           if (snowStrength > 0.001) {
             float e = 0.28;
             float h0 = snowDetail(uv);
             float hx = snowDetail(uv + vec2(e, 0.0));
             float hz = snowDetail(uv + vec2(0.0, e));
             // Gradient of the detail height field, straight into the normal.
             grad += vec3(-(hx - h0) / e, 0.0, -(hz - h0) / e) * snowStrength;
           }
           if (rockStrength > 0.004) {
             // Wider sample step to match the coarser field — reusing the snow
             // epsilon here would just alias it.
             float e = 0.75;
             float h0 = rockDetail(uv);
             float hx = rockDetail(uv + vec2(e, 0.0));
             float hz = rockDetail(uv + vec2(0.0, e));
             grad += vec3(-(hx - h0) / e, 0.0, -(hz - h0) / e) * rockStrength;
           }
           // The photographed normal map rides the same world-plane
           // projection as the procedural detail, one octave finer than the
           // ripple field.
           if (uSnowTexOn > 0.5 && snowStrength > 0.001) {
             vec3 tn = texture2D(uSnowNormalMap, uv / 3.1).xyz * 2.0 - 1.0;
             grad += vec3(tn.x, 0.0, tn.y) * (0.65 * detailFade * flSnowMask);
           }
           if (dot(grad, grad) > 1e-8) {
             // The shading normal is in view space here, the gradient is in
             // world space; rotate before combining or the detail slides around
             // as the camera turns.
             normal = normalize(normal + mat3(viewMatrix) * grad);
           }
         }`,
      )

    // --- Subsurface scattering ---------------------------------------------
    // Snow is not a Lambertian surface and this is the single biggest reason
    // rendered snow reads as white plaster. It is a dense, strongly
    // forward-scattering medium: light enters, bounces among the grains for a
    // few centimetres, and leaves somewhere near where it arrived. Three
    // consequences are visible in any snow photograph and in none of the
    // stock BRDF:
    //
    //   - the lit-to-shadow terminator is far softer than a hard N.L falloff
    //   - light bleeds a little way past the terminator instead of stopping
    //   - thin features backlit by a low sun glow — cornices, wind lips, and
    //     on this mountain the flutes cut into every corridor wall
    //
    // Wrapped diffuse buys the first two for a couple of instructions, and a
    // forward-scatter lobe along the light direction buys the third. Both are
    // energy-conserving enough not to disturb the exposure the scene is tuned
    // around: the wrap divides by (1 + w), and the transmission term is scaled
    // well below unity.
    // The lighting model lives inside a chunk, and inside `onBeforeCompile`
    // the shader source still has `#include <lights_physical_pars_fragment>`
    // sitting there *unexpanded* — three resolves chunks afterwards, in
    // WebGLProgram. So the chunk body is not in the string yet and nothing that
    // targets it can possibly match. Three attempts failed on that before the
    // mustReplace guard made it say so instead of compiling cleanly and quietly
    // doing nothing.
    //
    // Expand the chunk here, patch the copy, and substitute it for the include.
    // Patching THREE.ShaderChunk directly would work too and would change the
    // lighting of every material in the scene, which is not what is wanted.
    const scatterChunk = mustReplace(
      THREE.ShaderChunk.lights_physical_pars_fragment,
      'vec3 irradiance = dotNL * directLight.color;',
      [
        '// Wrapped diffuse: the terminator softens and light carries past it.',
        'float snowWrap = saturate( ( dot( geometryNormal, directLight.direction )',
        '  + FL_SNOW_WRAP ) / ( 1.0 + FL_SNOW_WRAP ) );',
        'vec3 irradiance = snowWrap * directLight.color;',
        '// Forward scatter: looking toward the sun through a thin edge, light',
        '// comes out the back of it. This is what makes a lip or a flute glow.',
        'float snowBack = saturate( dot( geometryViewDir, -directLight.direction ) );',
        'irradiance += directLight.color * pow( snowBack, 3.0 ) * FL_SNOW_TRANSMIT;',
      ].join('\n'),
      'snow subsurface scattering',
    )
    shader.fragmentShader = mustReplace(
      shader.fragmentShader,
      '#include <lights_physical_pars_fragment>',
      [
        '#define FL_SNOW_WRAP 0.45',
        '#define FL_SNOW_TRANSMIT 0.30',
        scatterChunk,
      ].join('\n'),
      'snow scattering chunk',
    )
  }

  // Force a distinct program from any other MeshStandardMaterial in the scene.
  mat.customProgramCacheKey = () => 'fall-line-snow'
  return mat
}
